// src/lib/dates.ts — date handling for a trip that crosses time zones.
//
// Everything is stored as timestamptz and rendered in the device's local zone,
// which is the right default: an expense logged at dinner in Chengdu should
// read as that evening while you are in Chengdu. It will re-group by a
// different day once you fly home. That is a real wrinkle and not one worth
// building a per-trip timezone setting for on this deadline.

const DAY = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});

const DAY_WITH_YEAR = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

export function parseDate(value: string | Date): Date {
  return value instanceof Date ? value : new Date(value);
}

/** "Tue, 14 Sep" — the heading over a day's expenses. */
export function formatDayHeading(value: string | Date): string {
  const date = parseDate(value);
  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  if (isToday) return 'Today';
  if (isYesterday) return 'Yesterday';
  return date.getFullYear() === today.getFullYear() ? DAY.format(date) : DAY_WITH_YEAR.format(date);
}

/** Stable key for grouping expenses into days in the device's local zone. */
export function dayKey(value: string | Date): string {
  const date = parseDate(value);
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}

/** "14 Sep 2026" */
export function formatDate(value: string | Date): string {
  return DAY_WITH_YEAR.format(parseDate(value));
}

/** A local datetime string an <input type="datetime-local"> will accept. */
export function toLocalInputValue(value: string | Date): string {
  const date = parseDate(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/** The reverse, for writing back to timestamptz. */
export function fromLocalInputValue(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
}

/** The local calendar date, as YYYY-MM-DD, for fx_rates.as_of lookups. */
export function toDateOnly(value: string | Date): string {
  const date = parseDate(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
